// ============================================================================
// psyReportsFilter.ts — ОТБОРЫ отчёта и протоколов по периоду/пациенту.
//
// Вынесено из lib/psyReports.ts (SRP): фильтрация — чистая логика без RPC.
// В ОТЧЁТЕ отбор применяется только при наличии подходящей колонки (дата /
// клиент); если колонки нет — строки не меняются, а панель честно сообщает,
// что отбор действует лишь на протоколы. Итоги (groupFooter/grandTotal) при
// отборе НЕ показываются: их агрегаты посчитаны по всем строкам.
// ============================================================================

import type { PsyProtocolRecord, PsyReportResult, PsyReportRow } from '@/lib/psyReports';

export interface PsyReportFilters {
  patientId: string;
  patientName: string;
  /** YYYY-MM-DD (включительно). */
  from: string;
  to: string;
}

export interface PsyFilteredReport {
  rows: PsyReportRow[];
  filtered: boolean;
  patientApplied: boolean;
  periodApplied: boolean;
  /** Итоги не показаны: применены отборы (агрегаты были бы по всем строкам). */
  totalsHidden: boolean;
}

const GROUP_TYPES = new Set(['groupHeader', 'groupFooter', 'grandTotal']);
const DATE_COL_RE = /дата|date|период|period/i;
const CLIENT_COL_RE = /клиент|пациент|client|patient|subject/i;

function norm(value: unknown): string {
  return String(value ?? '').trim().toLowerCase().replace(/ё/g, 'е');
}

function asDay(value: unknown): string {
  const s = String(value ?? '').trim();
  if (!s) return '';
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const ru = s.match(/^(\d{2})\.(\d{2})\.(\d{4})/);
  if (ru) return `${ru[3]}-${ru[2]}-${ru[1]}`;
  const t = Date.parse(s);
  if (!Number.isNaN(t)) return new Date(t).toISOString().slice(0, 10);
  return '';
}

/** Отбор протоколов: пациент (subject_id) + период по дате (включительно). */
export function filterPsyProtocols(items: PsyProtocolRecord[], filters: PsyReportFilters): PsyProtocolRecord[] {
  const from = filters.from || '';
  const to = filters.to || '';
  return items.filter((record) => {
    if (filters.patientId && record.patientId !== filters.patientId) return false;
    if (!from && !to) return true;
    const day = asDay(record.date);
    if (!day) return false;
    if (from && day < from) return false;
    if (to && day > to) return false;
    return true;
  });
}

/** Кандидат-колонка отчёта: первая подходящая по имени. */
function findColumn(columns: string[], re: RegExp): string {
  return columns.find((c) => re.test(c)) || '';
}

/**
 * Отбор строк отчёта по пациенту/периоду. Применяется ТОЛЬКО если в отчёте есть
 * соответствующая колонка (дата/клиент). Итоги (groupFooter/grandTotal) при
 * отборе не показываются — агрегаты были бы посчитаны по всем строкам.
 */
export function filterPsyReportResult(
  result: PsyReportResult,
  filters: PsyReportFilters,
  patientsById: Map<string, string>,
): PsyFilteredReport {
  const active = !!(filters.patientId || filters.from || filters.to);
  if (!active) {
    return { rows: result.rows, filtered: false, patientApplied: false, periodApplied: false, totalsHidden: false };
  }

  const patientCol = findColumn(result.columns, CLIENT_COL_RE);
  const dateCol = findColumn(result.columns, DATE_COL_RE);
  const patientApplied = !!filters.patientId && patientCol !== '';
  const periodApplied = !!(filters.from || filters.to) && dateCol !== '';
  if (!patientApplied && !periodApplied) {
    return { rows: result.rows, filtered: false, patientApplied: false, periodApplied: false, totalsHidden: false };
  }

  const patientName = norm(filters.patientName);
  const matches = (row: PsyReportRow): boolean => {
    const data = row.data || {};
    if (patientApplied) {
      const value = String(data[patientCol] ?? '');
      const patientLabel = norm(patientsById.get(value) || '');
      const byId = value !== '' && value === filters.patientId;
      const byName = patientName !== '' && (norm(value).includes(patientName) || patientLabel.includes(patientName));
      if (!byId && !byName) return false;
    }
    if (periodApplied) {
      const day = asDay(data[dateCol]);
      if (!day) return false;
      if (filters.from && day < filters.from) return false;
      if (filters.to && day > filters.to) return false;
    }
    return true;
  };

  const keep = new Set<number>();
  const groupKeep: Array<{ header: number; keep: boolean } | undefined> = [];
  result.rows.forEach((row, index) => {
    if (row.type === 'groupHeader') {
      groupKeep[row.level] = { header: index, keep: false };
      // Вложенные группы предыдущего уровня закрываются — их заголовки уже решены.
      for (let i = groupKeep.length - 1; i > row.level; i--) {
        const nested = groupKeep[i];
        if (nested?.keep) keep.add(nested.header);
        groupKeep[i] = undefined;
      }
      return;
    }
    if (GROUP_TYPES.has(row.type)) return;
    if (!matches(row)) return;
    keep.add(index);
    for (const group of groupKeep) if (group) group.keep = true;
  });
  for (const group of groupKeep) if (group?.keep) keep.add(group.header);

  const rows = result.rows.filter((row, index) => !GROUP_TYPES.has(row.type) && keep.has(index));
  return { rows, filtered: true, patientApplied, periodApplied, totalsHidden: true };
}
